import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { ScrollView, TouchableOpacity, Share, Alert, StyleSheet, Text as RNText, View as RNView } from 'react-native';
import { YStack, XStack, Text, H4, SizableText, Tabs, View, H6 } from 'tamagui';
import { MaterialIcons, FontAwesome } from '@expo/vector-icons';
import AnimateMarkup from '@hooks/AnimateMarkup';
import moment from 'moment';
import DefaultScreen from '@components/Global/DefaultScreenView';
import AvatarHandler from '@components/Global/AvatarHandler/AvatarHandler';
import globalStyles from '@stylesheet';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { ActionChips, ActionDataForm, PostExecutionFeedback } from '@components/ActionConfirmation';
import type { ActionTemplate, MatchedAction } from '@utils/semanticActionMapper';
import { analyzeResponse, isMapperReady } from '@utils/semanticActionMapper';
import { isSemanticActionMapperReady } from '@utils/semanticActionMapperSingleton';
import { extractDataHybrid } from '@utils/nlpDataExtractor';
import { getSchemaForAction } from '@utils/schemaCache';
import { submitFeedback, preloadFeedbackData } from '@utils/actionFeedbackManager';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import useGlobalUser from '@hooks/getGlobalUser';
import Markdown from 'react-native-markdown-display';
import { rules } from '@utils/stringUtils/markDownRules';
import MaskedView from '@react-native-masked-view/masked-view';
import { LinearGradient } from 'expo-linear-gradient';

// Gradient Text Component with fallback
const GradientText = ({ children }) => {
  // Handle different types of children properly
  let textContent = '';

  if (typeof children === 'string') {
    textContent = children;
  } else if (Array.isArray(children)) {
    textContent = children.map(child =>
      typeof child === 'string' ? child :
        typeof child === 'object' && child?.props?.children ?
          (typeof child.props.children === 'string' ? child.props.children : '') :
          ''
    ).join('');
  } else if (children && typeof children === 'object' && children.props && children.props.children) {
    textContent = typeof children.props.children === 'string' ? children.props.children : '';
  } else {
    textContent = String(children);
  }

  // Use solid color with shadow for gradient-like effect
  return (
    <RNText style={{
      color: '#6366f1',
      fontWeight: '800',
      textShadowColor: '#a855f7',
      textShadowOffset: { width: 1, height: 1 },
      textShadowRadius: 2,
    }}>
      {textContent}
    </RNText>
  );
};

const MeetingPage = ({ route }) => {
  const { _id, title, createdAt, createdBy, summary, transcript } = route.params;
  const [activeTab, setActiveTab] = useState('summary');
  const [animationComplete, setAnimationComplete] = useState(false);
  const [detectedActions, setDetectedActions] = useState<MatchedAction[]>([]);
  const [isMapperInitialized, setIsMapperInitialized] = useState(false);
  const [actionRequiringData, setActionRequiringData] = useState<ActionTemplate | null>(null);
  const [extractedFormData, setExtractedFormData] = useState<Record<string, any>>({});
  const [showPostExecutionFeedback, setShowPostExecutionFeedback] = useState(false);
  const [lastExecutedAction, setLastExecutedAction] = useState<{ template: ActionTemplate; data?: any } | null>(null);
  const [showActionChips, setShowActionChips] = useState(true);

  const theme = themeSelector();
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const globalUser = useGlobalUser();

  // Reset state when leaving the meeting page
  useFocusEffect(
    useCallback(() => {
      // This runs when the screen comes into focus
      return () => {
        // Cleanup function runs when the screen loses focus (navigating away)
        console.log('🧹 [MeetingPage] Resetting state on navigation away');
        setActiveTab('summary');
        setAnimationComplete(false);
        setDetectedActions([]);
        setIsMapperInitialized(false);
        setActionRequiringData(null);
        setExtractedFormData({});
        setShowPostExecutionFeedback(false);
        setLastExecutedAction(null);
        setShowActionChips(true);
      };
    }, [])
  );

  // Check if semantic action mapper is ready
  useEffect(() => {
    const checkMapperReady = () => {
      const isReady = isSemanticActionMapperReady();
      console.log('🔍 [MeetingPage] Checking mapper ready status:', { isReady, isMapperInitialized });

      if (isReady && !isMapperInitialized) {
        setIsMapperInitialized(true);
        console.log('✅ [MeetingPage] Semantic action mapper is ready');
      } else if (!isReady) {
        setTimeout(checkMapperReady, 500);
      }
    };

    checkMapperReady();
  }, [isMapperInitialized]);

  // Preload feedback data when user is available
  useEffect(() => {
    if (globalUser?._id && isMapperInitialized) {
      const commonActionIntents = [
        'CREATE_TASK',
        'SCHEDULE_MEETING',
        'SEND_EMAIL',
        'CREATE_REMINDER',
        'CREATE_PROJECT',
        'CREATE_NOTE'
      ];
      preloadFeedbackData(commonActionIntents, globalUser._id);
    }
  }, [globalUser?._id, isMapperInitialized]);

  // Extract actual keywords found in the text that triggered actions
  const getHighlightedKeywords = useCallback((text: string) => {
    const foundKeywords: string[] = [];

    // Comprehensive list of action-trigger keywords
    const allKeywords = [
      // Multi-word phrases (check first to avoid partial matches)
      'action item', 'action items', 'follow up', 'next step', 'next steps',
      'next meeting', 'follow-up meeting', 'share with', 'due date',
      // Single words
      'todo', 'task', 'tasks', 'action', 'actions',
      'schedule', 'reschedule', 'meet', 'call', 'meeting', 'meetings',
      'email', 'send', 'notify', 'inform', 'contact',
      'deadline', 'reminder', 'reminders',
      'project', 'projects', 'initiative', 'plan', 'planning', 'program', 'proposal',
      'decision', 'decisions', 'agree', 'agreed', 'conclude', 'concluded', 'decide', 'decided', 'resolve', 'resolved', 'determine', 'determined',
      // Additional common action words
      'need', 'needs', 'will', 'should', 'must', 'have to', 'required',
      'discuss', 'review', 'check', 'verify', 'confirm', 'update', 'create'
    ];

    const lowerText = text.toLowerCase();

    console.log('🔍 [MeetingPage] Detected actions:', detectedActions.map(a => ({
      intent: a.intent,
      description: a.template?.description
    })));
    console.log('📝 [MeetingPage] Text to search (first 200 chars):', text.substring(0, 200));

    // Check which keywords actually exist in the text
    allKeywords.forEach(word => {
      const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`\\b${escapedWord}\\b`, 'i');
      if (regex.test(lowerText)) {
        if (!foundKeywords.includes(word)) {
          foundKeywords.push(word);
          console.log('✅ [MeetingPage] Found keyword:', word);
        }
      }
    });

    console.log('🎨 [MeetingPage] Total keywords to highlight:', foundKeywords);
    return foundKeywords;
  }, [detectedActions]);

  // Analyze summary using semantic action mapper
  useEffect(() => {
    if (summary && animationComplete && isMapperInitialized) {
      console.log('🔍 [MeetingPage] Analyzing meeting summary for actions...');
      analyzeResponse(summary).then(matches => {
        if (matches.length > 0) {
          console.log('🎯 [MeetingPage] Found', matches.length, 'actionable intents:',
            matches.map(m => ({
              intent: m.intent,
              description: m.template?.description,
              category: m.template?.category
            }))
          );
          setDetectedActions(matches);
        }
      }).catch(error => {
        console.error('❌ [MeetingPage] Failed to analyze summary:', error);
      });
    }
  }, [summary, animationComplete, isMapperInitialized]);

  // Custom markdown rules with gradient highlights
  const customRules = useMemo(() => ({
    ...rules,
    strong: (node, children, parent, styles) => (
      <GradientText key={node.key}>
        {children}
      </GradientText>
    )
  }), []);

  // Function to highlight keywords in text with gradient
  const highlightKeywords = useCallback((text: string) => {
    if (!showActionChips || detectedActions.length === 0 || !text) {
      console.log('🔍 [MeetingPage] Not highlighting:', { showActionChips, detectedActionsCount: detectedActions.length, hasText: !!text });
      return text;
    }

    const keywords = getHighlightedKeywords(text);
    console.log('🔍 [MeetingPage] Keywords found in text:', keywords);

    if (keywords.length === 0) {
      console.log('⚠️ [MeetingPage] No keywords matched in text');
      return text;
    }

    // Sort keywords by length (longest first) to avoid partial matches
    const sortedKeywords = keywords.sort((a, b) => b.length - a.length);

    let highlightedText = text;
    let replacementCount = 0;

    sortedKeywords.forEach(keyword => {
      const regex = new RegExp(`(${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');

      // Use markdown bold for custom styling
      highlightedText = highlightedText.replace(regex, (match) => {
        replacementCount++;
        return `**${match}**`;
      });
    });

    console.log('✨ [MeetingPage] Highlighted', replacementCount, 'keyword instances');
    console.log('📝 [MeetingPage] First 200 chars of highlighted text:', highlightedText.substring(0, 200));

    return highlightedText;
  }, [showActionChips, detectedActions, getHighlightedKeywords]);

  const handleActionExecute = useCallback(async (template: ActionTemplate, data?: any) => {
    console.log('🎯 [MeetingPage] Execute action called:', {
      intent: template.intent,
      requiresData: template.requiresData,
      hasSchema: !!template.schema,
      hasData: !!data
    });

    // If action requires data and no data provided, try to extract it
    if (template.requiresData && !data) {
      const schema = getSchemaForAction(template.intent) || template.schema;

      if (schema && summary) {
        console.log('🔍 [MeetingPage] Attempting to extract data from summary...');
        const extraction = await extractDataHybrid(
          summary,
          schema,
          template.description || template.intent
        );

        console.log('📊 [MeetingPage] Extraction result:', extraction);

        if (extraction.confidence > 0.6 && extraction.missingFields.length === 0) {
          console.log('✅ [MeetingPage] Auto-filling with extracted data');
          data = extraction.extractedData;
        } else if (extraction.confidence > 0.3) {
          console.log('📝 [MeetingPage] Pre-filling form with partial data');
          setExtractedFormData(extraction.extractedData);
          setActionRequiringData(template);
          return;
        } else {
          console.log('❌ [MeetingPage] Low confidence extraction, showing empty form');
          setExtractedFormData({});
          setActionRequiringData(template);
          return;
        }
      } else {
        setActionRequiringData(template);
        return;
      }
    }

    // Execute the action
    try {
      const executionData = data;

      if (template.actionSequence && template.actionSequence.length > 0) {
        console.log('🔄 [MeetingPage] Executing action sequence');
        template.actionSequence.forEach((actionFn, index) => {
          setTimeout(() => {
            const action = actionFn(executionData);
            console.log(`🎯 Action ${index + 1}/${template.actionSequence!.length}:`, action.type);

            if (action.type === 'REACT_NAVIGATE') {
              const { screen, stack, params } = action.payload;
              if (stack) {
                (navigation as any).navigate(stack, { screen, params });
              } else {
                (navigation as any).navigate(screen, params);
              }
            } else {
              dispatch(action);
            }
          }, index * 100);
        });
      } else if (template.reduxAction) {
        const action = template.reduxAction(executionData);

        if (action.type === 'REACT_NAVIGATE') {
          const { screen, stack, params } = action.payload;
          if (stack) {
            (navigation as any).navigate(stack, { screen, params });
          } else {
            (navigation as any).navigate(screen, params);
          }
        } else {
          dispatch(action);
        }
      } else {
        // Fallback for actions without Redux action defined
        Alert.alert('Action Executed', template.description || template.intent);
      }

      // Remove from pending actions
      setDetectedActions(prev => prev.filter(a => a.intent !== template.intent));

      // Close data form if open
      if (actionRequiringData) {
        setActionRequiringData(null);
      }

      // Store executed action for post-execution feedback
      setLastExecutedAction({ template, data });

      // Show post-execution feedback modal after a short delay
      setTimeout(() => {
        setShowPostExecutionFeedback(true);
      }, 1000);

    } catch (error) {
      console.error('❌ [MeetingPage] Failed to execute action:', error);
      Alert.alert('Error', 'Failed to execute action');
    }
  }, [dispatch, navigation, actionRequiringData, summary]);

  const handleActionDismiss = useCallback((intent: string) => {
    setDetectedActions(prev => prev.filter(action => action.intent !== intent));
  }, []);

  const handleActionDismissAll = useCallback(() => {
    setDetectedActions([]);
  }, []);

  const handleDataFormSubmit = useCallback((template: ActionTemplate, data: any) => {
    handleActionExecute(template, data);
  }, [handleActionExecute]);

  const handleDataFormCancel = useCallback(() => {
    setActionRequiringData(null);
  }, []);

  const handleActionFeedback = useCallback(async (actionIntent: string, feedbackType: 'upvote' | 'downvote') => {
    try {
      const success = await submitFeedback({
        actionIntent,
        query: summary || title,
        feedbackType,
        wasExecuted: false,
        context: {
          screen: 'MeetingPage',
          companyId: globalUser?.currentCompany?._id,
          additionalData: {
            source: 'inline_chip',
            meetingId: _id
          }
        }
      });

      if (success) {
        console.log('✅ [MeetingPage] Inline feedback submitted successfully');
      }
    } catch (error) {
      console.error('❌ [MeetingPage] Failed to submit inline feedback:', error);
    }
  }, [summary, title, globalUser, _id]);

  const handlePostExecutionFeedback = useCallback(async (feedbackType: 'upvote' | 'downvote', comment?: string) => {
    if (!lastExecutedAction) return;

    try {
      const success = await submitFeedback({
        actionIntent: lastExecutedAction.template.intent,
        query: summary || title,
        feedbackType,
        wasExecuted: true,
        executionSuccess: feedbackType === 'upvote',
        context: {
          screen: 'MeetingPage',
          companyId: globalUser?.currentCompany?._id,
          additionalData: {
            source: 'post_execution',
            comment: comment || null,
            meetingId: _id
          }
        }
      });

      if (success) {
        console.log('✅ [MeetingPage] Post-execution feedback submitted successfully');
      }
    } catch (error) {
      console.error('❌ [MeetingPage] Failed to submit post-execution feedback:', error);
    }
  }, [lastExecutedAction, summary, title, globalUser, _id]);

  const handleShare = useCallback(async () => {
    try {
      await Share.share({
        message: `Meeting: ${title}\n\nSummary:\n${summary}\n\nTranscript:\n${transcript}`,
        title: title || 'Meeting Notes',
      });
    } catch (error) {
      Alert.alert('Error', 'Failed to share meeting');
    }
  }, [title, summary, transcript]);

  const handleResummarize = useCallback(() => {
    Alert.alert('Re-summarize', 'This feature will re-generate the meeting summary');
    // TODO: Implement re-summarize functionality
  }, []);

  return (
    <DefaultScreen
      backButton
      scrollDisabled
      style={[globalStyles.modernMainContent, { paddingBottom: 0 }]}
      containerStyle={{ top: -30 }}
    >
      <YStack flex={1} gap="$4" top={-36} paddingHorizontal={8} paddingBottom={16}>
        {/* Header Card */}
        <View style={[
          globalStyles.modernCard,
          theme ? globalStyles.modernBgLight : globalStyles.modernBgDark,
          { marginTop: 16, padding: 0 }
        ]}>
          <YStack gap="$3" padding={20}>
            <XStack alignItems="flex-start" justifyContent="space-between">
              <YStack flex={1} gap="$2">
                <H4 style={{
                  color: theme ? '#1e293b' : '#f1f5f9',
                  fontWeight: '700',
                  fontSize: 22
                }}>
                  {title || 'Untitled Meeting'}
                </H4>

                <XStack gap="$3" alignItems="center">
                  <XStack gap="$1" alignItems="center">
                    <MaterialIcons
                      name="event"
                      size={16}
                      color={theme ? '#64748b' : '#94a3b8'}
                    />
                    <Text style={{
                      color: theme ? '#64748b' : '#94a3b8',
                      fontSize: 14
                    }}>
                      {moment(createdAt).format("MMM Do, YYYY")}
                    </Text>
                  </XStack>

                  <XStack gap="$1" alignItems="center">
                    <MaterialIcons
                      name="access-time"
                      size={16}
                      color={theme ? '#64748b' : '#94a3b8'}
                    />
                    <Text style={{
                      color: theme ? '#64748b' : '#94a3b8',
                      fontSize: 14
                    }}>
                      {moment(createdAt).format("h:mm A")}
                    </Text>
                  </XStack>
                </XStack>
              </YStack>

              <View style={{ marginLeft: 16 }}>
                <AvatarHandler
                  avatarImageStyle={{
                    width: 56,
                    height: 56,
                    borderRadius: 16
                  }}
                  replacementImageStyle={{
                    width: 56,
                    height: 56,
                    borderRadius: 16
                  }}
                  avatar={createdBy?.profile_information?.avatar?.img}
                />
              </View>
            </XStack>

            {/* Action Buttons */}
            <XStack gap="$3" marginTop={12} width="100%">
              <TouchableOpacity
                style={[
                  globalStyles.modernActionButton,
                  theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark,
                  localStyles.actionButton
                ]}
                onPress={handleShare}
              >
                <MaterialIcons name="share" size={20} color="white" />
                <Text style={{ color: 'white', marginLeft: 10, fontSize: 15, fontWeight: '600' }}>
                  Share
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  globalStyles.modernActionButton,
                  theme ? globalStyles.modernActionAmber : globalStyles.modernActionAmberDark,
                  localStyles.actionButton
                ]}
                onPress={handleResummarize}
              >
                <FontAwesome name="refresh" size={18} color="white" />
                <Text style={{ color: 'white', marginLeft: 10, fontSize: 15, fontWeight: '600' }}>
                  Re-summarize
                </Text>
              </TouchableOpacity>

              {/* Action Chips Toggle Button */}
              {detectedActions.length > 0 && (
                <TouchableOpacity
                  style={[
                    globalStyles.modernActionButton,
                    theme ? globalStyles.modernActionGreen : globalStyles.modernActionGreenDark,
                    localStyles.actionButton,
                    { position: 'relative' }
                  ]}
                  onPress={() => setShowActionChips(!showActionChips)}
                >
                  <MaterialIcons
                    name={showActionChips ? "lightbulb" : "lightbulb-outline"}
                    size={20}
                    color="white"
                  />
                  <Text style={{ color: 'white', marginLeft: 10, fontSize: 15, fontWeight: '600' }}>
                    Actions
                  </Text>
                  {detectedActions.length > 0 && (
                    <View style={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      backgroundColor: '#ef4444',
                      borderRadius: 10,
                      minWidth: 20,
                      height: 20,
                      justifyContent: 'center',
                      alignItems: 'center',
                      paddingHorizontal: 6
                    }}>
                      <Text style={{ color: 'white', fontSize: 11, fontWeight: '700' }}>
                        {detectedActions.length}
                      </Text>
                    </View>
                  )}
                </TouchableOpacity>
              )}
            </XStack>
          </YStack>
        </View>

        {/* Tabs Content */}
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          flexDirection="column"
          flex={1}
          style={{ flex: 1 }}
        >
          <Tabs.List
            separator={<View width={8} />}
            paddingHorizontal={0}
            backgroundColor="transparent"
            minWidth="100%"
            alignSelf="stretch"
          >
            <Tabs.Tab
              value="summary"
              flex={1}
              paddingVertical={10}
              paddingHorizontal={16}
              borderRadius={12}
              gap="$2"
              backgroundColor={activeTab === 'summary' ? 'rgb(90,192,234)' : (theme ? '#f1f5f9' : '#1e293b')}
              pressStyle={{
                backgroundColor: activeTab === 'summary' ? 'rgb(90,192,234)' : (theme ? '#f1f5f9' : '#1e293b')
              }}
              hoverStyle={{
                backgroundColor: activeTab === 'summary' ? 'rgb(90,192,234)' : (theme ? '#f1f5f9' : '#1e293b')
              }}
              focusStyle={{
                backgroundColor: activeTab === 'summary' ? 'rgb(90,192,234)' : (theme ? '#f1f5f9' : '#1e293b')
              }}
              style={{
                backgroundColor: activeTab === 'summary' ? 'rgb(90,192,234)' : (theme ? '#f1f5f9' : '#1e293b')
              }}
            >
              <XStack gap="$2" alignItems="center">
                <MaterialIcons
                  name="summarize"
                  size={18}
                  color={activeTab === 'summary' ? 'white' : (theme ? '#64748b' : '#94a3b8')}
                />
                <SizableText
                  color={activeTab === 'summary' ? 'white' : (theme ? '#64748b' : '#94a3b8')}
                  fontWeight={activeTab === 'summary' ? '700' : '600'}
                  fontSize={15}
                >
                  Summary
                </SizableText>
              </XStack>
            </Tabs.Tab>

            <Tabs.Tab
              value="transcript"
              flex={1}
              paddingVertical={10}
              paddingHorizontal={16}
              borderRadius={12}
              gap="$2"
              backgroundColor={activeTab === 'transcript' ? 'rgb(90,192,234)' : (theme ? '#f1f5f9' : '#1e293b')}
              pressStyle={{
                backgroundColor: activeTab === 'transcript' ? 'rgb(90,192,234)' : (theme ? '#f1f5f9' : '#1e293b')
              }}
              hoverStyle={{
                backgroundColor: activeTab === 'transcript' ? 'rgb(90,192,234)' : (theme ? '#f1f5f9' : '#1e293b')
              }}
              focusStyle={{
                backgroundColor: activeTab === 'transcript' ? 'rgb(90,192,234)' : (theme ? '#f1f5f9' : '#1e293b')
              }}
              style={{
                backgroundColor: activeTab === 'transcript' ? 'rgb(90,192,234)' : (theme ? '#f1f5f9' : '#1e293b')
              }}
            >
              <XStack gap="$2" alignItems="center">
                <MaterialIcons
                  name="article"
                  size={18}
                  color={activeTab === 'transcript' ? 'white' : (theme ? '#64748b' : '#94a3b8')}
                />
                <SizableText
                  color={activeTab === 'transcript' ? 'white' : (theme ? '#64748b' : '#94a3b8')}
                  fontWeight={activeTab === 'transcript' ? '700' : '600'}
                  fontSize={15}
                >
                  Transcript
                </SizableText>
              </XStack>
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Content
            value="summary"
            flex={1}
            paddingVertical={16}
            paddingBottom={24}
            maxHeight="90%"
          >
            <YStack flex={1} gap="$3">
              {/* Collapsible Action Chips */}
              {detectedActions.length > 0 && showActionChips && (
                <View style={{
                  marginBottom: 8,
                  overflow: 'hidden'
                }}>
                  <ActionChips
                    actions={detectedActions}
                    onExecute={handleActionExecute}
                    onDismiss={handleActionDismiss}
                    onDismissAll={handleActionDismissAll}
                    onFeedback={handleActionFeedback}
                    disableAbsolutePositioning={true}
                  />
                </View>
              )}

              {/* Summary Content */}
              <View style={[
                globalStyles.modernCard,
                theme ? globalStyles.modernBgLight : globalStyles.modernBgDark,
                { flex: 1 }
              ]}>
                <ScrollView
                  style={{ flex: 1 }}
                  contentContainerStyle={{ padding: 20 }}
                  showsVerticalScrollIndicator={false}
                >
                  <H6>Summary</H6>
                  {summary ? (
                    <>
                      {showActionChips && detectedActions.length > 0 && animationComplete ? (
                        <Markdown
                          key={`summary-highlighted-${detectedActions.length}-${showActionChips}`}
                          rules={customRules}
                          style={{
                            body: {
                              lineHeight: 22,
                              color: theme ? '#1e293b' : '#f1f5f9'
                            },
                            bullet_list: { marginVertical: 4 },
                            ordered_list: { marginVertical: 4 },
                            paragraph: {
                              lineHeight: 22,
                              marginVertical: 6,
                              color: theme ? '#1e293b' : '#f1f5f9'
                            }
                          }}
                        >
                          {highlightKeywords(summary)}
                        </Markdown>
                      ) : (
                        <AnimateMarkup
                          key={`summary-animated-${showActionChips}`}
                          value={summary}
                          speed={20}
                          disabledAnimation={animationComplete}
                          onAnimationComplete={() => setAnimationComplete(true)}
                        />
                      )}
                    </>
                  ) : (
                    <YStack alignItems="center" justifyContent="center" padding={40}>
                      <MaterialIcons
                        name="summarize"
                        size={48}
                        color={theme ? '#cbd5e1' : '#475569'}
                      />
                      <Text style={{
                        color: theme ? '#64748b' : '#94a3b8',
                        marginTop: 16,
                        fontSize: 16,
                        textAlign: 'center'
                      }}>
                        No summary available
                      </Text>
                    </YStack>
                  )}
                </ScrollView>
              </View>
            </YStack>
          </Tabs.Content>

          <Tabs.Content
            value="transcript"
            flex={1}
            paddingVertical={16}
            paddingBottom={24}
            maxHeight="90%"
          >
            <View style={[
              globalStyles.modernCard,
              theme ? globalStyles.modernBgLight : globalStyles.modernBgDark,
              { flex: 1 }
            ]}>
              <ScrollView
                style={{ flex: 1 }}
                contentContainerStyle={{ padding: 20 }}
                showsVerticalScrollIndicator={false}
              >
                <H6>Transcript</H6>
                {transcript ? (
                  <>
                    {showActionChips && detectedActions.length > 0 ? (
                      <Markdown
                        key={`transcript-highlighted-${detectedActions.length}-${showActionChips}`}
                        rules={customRules}
                        style={{
                          body: {
                            lineHeight: 24,
                            fontSize: 15,
                            color: theme ? '#1e293b' : '#f1f5f9'
                          },
                          bullet_list: { marginVertical: 4 },
                          ordered_list: { marginVertical: 4 },
                          paragraph: {
                            lineHeight: 24,
                            marginVertical: 6,
                            fontSize: 15,
                            color: theme ? '#1e293b' : '#f1f5f9'
                          }
                        }}
                      >
                        {highlightKeywords(transcript)}
                      </Markdown>
                    ) : (
                      <SizableText
                        key={`transcript-plain-${showActionChips}`}
                        style={{
                          color: theme ? '#1e293b' : '#f1f5f9',
                          lineHeight: 24,
                          fontSize: 15
                        }}
                      >
                        {transcript}
                      </SizableText>
                    )}
                  </>
                ) : (
                  <YStack alignItems="center" justifyContent="center" padding={40}>
                    <MaterialIcons
                      name="article"
                      size={48}
                      color={theme ? '#cbd5e1' : '#475569'}
                    />
                    <Text style={{
                      color: theme ? '#64748b' : '#94a3b8',
                      marginTop: 16,
                      fontSize: 16,
                      textAlign: 'center'
                    }}>
                      No transcript available
                    </Text>
                  </YStack>
                )}
              </ScrollView>
            </View>
          </Tabs.Content>
        </Tabs>
      </YStack>

      {/* Action Data Form Modal */}
      <ActionDataForm
        visible={actionRequiringData !== null}
        actionTemplate={actionRequiringData}
        onSubmit={handleDataFormSubmit}
        onCancel={handleDataFormCancel}
        initialData={extractedFormData}
      />

      {/* Post Execution Feedback Modal */}
      <PostExecutionFeedback
        visible={showPostExecutionFeedback}
        actionDescription={lastExecutedAction?.template?.description || lastExecutedAction?.template?.intent || 'Action'}
        onFeedback={handlePostExecutionFeedback}
        onDismiss={() => {
          setShowPostExecutionFeedback(false);
          setLastExecutedAction(null);
        }}
        autoDismissAfter={5000}
      />
    </DefaultScreen>
  );
};

const localStyles = StyleSheet.create({
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
    minHeight: 48,
  },
  tab: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 0,
    overflow: 'hidden',
  },
  activeTab: {
    shadowColor: '#000',
    shadowRadius: 8,
    shadowOpacity: 0.15,
    shadowOffset: { width: 0, height: 2 },
    elevation: 4,
  },
  activeTabLight: {
  },
  activeTabDark: {
  },
  inactiveTabLight: {
  },
  inactiveTabDark: {
  },
});

export default MeetingPage;